import { useState, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ColorSwatch } from './ColorSwatch';
import { searchColors, SHERWIN_WILLIAMS_COLORS } from '@/data/sherwin-williams-colors';
import type { SherwinWilliamsColor } from '@/types/firestore';
import { cn } from '@/lib/utils';

interface ColorSearchProps {
  onSelect: (color: SherwinWilliamsColor) => void;
  selectedColor?: SherwinWilliamsColor;
  customColors?: SherwinWilliamsColor[];
  className?: string;
}

export function ColorSearch({ onSelect, selectedColor, customColors = [], className }: ColorSearchProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const normalizedQuery = query.toLowerCase().trim();

    // Search custom colors first (tenant-specific)
    const customResults = customColors.filter(
      (color) =>
        color.name.toLowerCase().includes(normalizedQuery) ||
        color.code.toLowerCase().includes(normalizedQuery) ||
        (color.family && color.family.toLowerCase().includes(normalizedQuery))
    );

    // Search default colors
    const defaultResults = searchColors(query);

    // Combine results, custom colors first, remove duplicates by code, limit to 12
    const seen = new Set<string>();
    const combined: SherwinWilliamsColor[] = [];

    for (const color of [...customResults, ...defaultResults]) {
      if (!seen.has(color.code) && combined.length < 12) {
        seen.add(color.code);
        combined.push(color);
      }
    }

    return combined;
  }, [query, customColors]);

  const handleSelect = (color: SherwinWilliamsColor) => {
    onSelect(color);
    setQuery('');
    setIsOpen(false);
  };

  return (
    <div className={cn('relative', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search all Sherwin-Williams colors..."
          className="pl-9 pr-9"
        />
        {query && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={() => {
              setQuery('');
              setIsOpen(false);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-lg z-50 max-h-[300px] overflow-y-auto">
          <div className="p-3 grid grid-cols-3 gap-2">
            {results.map((color) => (
              <ColorSwatch
                key={color.code}
                color={color}
                size="sm"
                isSelected={selectedColor?.code === color.code}
                onClick={() => handleSelect(color)}
              />
            ))}
          </div>
          <div className="border-t px-3 py-2 text-xs text-muted-foreground text-center">
            {results.length} colors found
          </div>
        </div>
      )}

      {/* No Results */}
      {isOpen && query && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-lg z-50 p-4 text-center text-sm text-muted-foreground">
          No colors found matching "{query}"
        </div>
      )}
    </div>
  );
}
